import { useEffect, useState } from 'react';
import type { IntakePending, ProductListItem, StockWarehouseRef } from '@kassa/shared';
import { api, ApiError } from '../api';

interface Props {
  onBack: () => void;
}

// «Malı aldım» (KT-62): anbardar qaiməyə görə sayır (qismən də), «Qəbul bitdi» fərq aktı yaradır.
// Qaiməsiz qəbul: yalnız mal və miqdar; qiyməti mühasib yazır. Qiymət və pul göstərilmir.
export function Intake({ onBack }: Props) {
  const [pending, setPending] = useState<IntakePending[] | null>(null);
  const [counts, setCounts] = useState<Record<string, { qty: string; damaged: string }>>({});
  const [finals, setFinals] = useState<Record<string, boolean>>({});
  const [ids, setIds] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function reload() {
    return api
      .intakePending()
      .then((p) => {
        setPending(p);
        setCounts(Object.fromEntries(p.flatMap((d) => d.lines.map((l) => [l.lineId, { qty: String(Math.max(0, l.expected - l.received)), damaged: '0' }]))));
        setError(null);
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Siyahı yüklənmədi'));
  }

  useEffect(() => {
    reload();
  }, []);

  async function receive(doc: IntakePending) {
    const lines = doc.lines.map((l) => ({ lineId: l.lineId, qty: Number(counts[l.lineId]?.qty) || 0, damaged: Number(counts[l.lineId]?.damaged) || 0 }));
    const id = ids[doc.docId] ?? crypto.randomUUID();
    setIds((cur) => ({ ...cur, [doc.docId]: id }));
    setBusy(true);
    setError(null);
    try {
      const r = await api.intakeReceive(doc.docId, { id, lines, final: finals[doc.docId] ?? true });
      setNotice(`${doc.displayNo}: qəbul yazıldı (${r.displayNo})${r.diffs > 0 ? ` — fərq aktı: ${r.diffs} sətir` : ''}`);
      setIds((cur) => {
        const next = { ...cur };
        delete next[doc.docId];
        return next;
      });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Qəbul alınmadı. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="shell">
      <header className="topbar">
        <span className="brand">Malı aldım</span>
        <span className="topbar-spacer" />
        <button type="button" className="link-btn back-home" onClick={onBack}>
          Geri
        </button>
      </header>
      <main className="content">
        <section className="card card-wide">
          <h2>Malı aldım</h2>
          <p className="muted small">Qaiməyə görə sayın. Qismən qəbul edə bilərsiniz; «Qəbul bitdi» seçiləndə fərq olan sətirlər mühasibə gedir.</p>
          {notice && <p className="ok" aria-live="polite">{notice}</p>}
          {error && (
            <p className="bad" role="alert">
              {error}
            </p>
          )}
          {!pending && !error && <p className="muted">Yüklənir…</p>}
          {pending && pending.length === 0 && <p className="muted">Qəbul gözləyən alış yoxdur.</p>}
          {pending?.map((doc) => (
            <div key={doc.docId} className="card">
              <p>
                <strong>{doc.supplierName}</strong> · {doc.displayNo} · {doc.warehouseName}
              </p>
              <ul className="product-list">
                {doc.lines.map((l) => (
                  <li key={l.lineId} className="product-row">
                    <span className="product-name">{l.productName}</span>
                    <span className="muted small">
                      gözlənilir {l.expected} · qəbul {l.received} {l.unit}
                    </span>
                    <label>
                      Sayılan
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        aria-label={`${l.productName} sayılan`}
                        value={counts[l.lineId]?.qty ?? ''}
                        onChange={(e) => setCounts((cur) => ({ ...cur, [l.lineId]: { ...(cur[l.lineId] ?? { qty: '0', damaged: '0' }), qty: e.target.value } }))}
                        disabled={busy}
                      />
                    </label>
                    <label>
                      Zədəli
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        aria-label={`${l.productName} zədəli`}
                        value={counts[l.lineId]?.damaged ?? ''}
                        onChange={(e) => setCounts((cur) => ({ ...cur, [l.lineId]: { ...(cur[l.lineId] ?? { qty: '0', damaged: '0' }), damaged: e.target.value } }))}
                        disabled={busy}
                      />
                    </label>
                  </li>
                ))}
              </ul>
              <label className="check">
                <input type="checkbox" checked={finals[doc.docId] ?? true} onChange={(e) => setFinals((cur) => ({ ...cur, [doc.docId]: e.target.checked }))} disabled={busy} /> Qəbul bitdi
              </label>
              <button type="button" className="btn-primary" onClick={() => receive(doc)} disabled={busy}>
                Qəbul et
              </button>
            </div>
          ))}
          <FreeIntake onDone={(msg) => { setNotice(msg); }} />
        </section>
      </main>
    </div>
  );
}

// Qaiməsiz qəbul: anbar, mal, miqdar
function FreeIntake({ onDone }: { onDone: (msg: string) => void }) {
  const [warehouses, setWarehouses] = useState<StockWarehouseRef[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<ProductListItem[]>([]);
  const [lines, setLines] = useState<{ productId: string; name: string; qty: string }[]>([]);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.stockWarehouses().then((w) => {
      setWarehouses(w);
      if (w.length > 0) setWarehouseId(w[0].id);
    }).catch(() => setWarehouses([]));
  }, []);

  useEffect(() => {
    if (search.trim().length < 2) return setFound([]);
    const t = setTimeout(() => {
      api.products({ q: search.trim(), limit: 10 }).then((r) => setFound(r.items)).catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  async function submit() {
    const body = lines.map((l) => ({ productId: l.productId, qty: Number(l.qty) })).filter((l) => l.qty > 0);
    if (!warehouseId || body.length === 0) return setError('Anbar və ən azı bir məhsul seçin');
    const id = attemptId ?? crypto.randomUUID();
    setAttemptId(id);
    setBusy(true);
    setError(null);
    try {
      const r = await api.intakeFree({ id, warehouseId, lines: body });
      setLines([]);
      setAttemptId(null);
      onDone(`${r.displayNo}: qaiməsiz qəbul yazıldı — qiyməti mühasib yazacaq`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Qəbul alınmadı. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h3>Qaiməsiz qəbul</h3>
      <div className="form-grid">
        <label>
          Anbar
          <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} disabled={busy}>
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.outletName} · {w.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Məhsul axtarışı
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ad, barkod və ya kod" disabled={busy} />
        </label>
      </div>
      {found.map((p) => (
        <button key={p.id} type="button" className="product-row" onClick={() => { setLines((cur) => (cur.some((l) => l.productId === p.id) ? cur : [...cur, { productId: p.id, name: p.name, qty: '1' }])); setSearch(''); setFound([]); }}>
          {p.name}
        </button>
      ))}
      <ul className="product-list">
        {lines.map((l, i) => (
          <li key={l.productId} className="product-row">
            <span className="product-name">{l.name}</span>
            <input type="number" min="0" step="0.001" aria-label={`${l.name} miqdar`} value={l.qty} onChange={(e) => setLines((cur) => cur.map((x, k) => (k === i ? { ...x, qty: e.target.value } : x)))} disabled={busy} />
          </li>
        ))}
      </ul>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      <button type="button" className="btn-small" onClick={submit} disabled={busy || lines.length === 0}>
        Qaiməsiz qəbul et
      </button>
    </section>
  );
}
